import {
    activateAbTestByEvent,
    activateAbTestById,
    getAbRegistrySnapshot,
    initAbRegistry,
    onAbRegistryUpdate,
} from './registry';

const DEBUG_FLAG_KEY = 'ab_debug_panel';
const PANEL_ID = 'ab-debug-panel';
const CLOSED_KEY = 'ab_debug_panel_closed';
const COLLAPSED_KEY = 'ab_debug_panel_collapsed';

const escapeHtml = (value: string): string => {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
};

// Code à coller dans l'éditeur JS d'une variante AB Tasty pour déclencher le test.
// Miroir de la logique d'activation du bouton "Activer" (voir plus bas).
const buildAbTastyCode = (id: string, eventName: string, source: string): string => {
    const isRealEvent = eventName && eventName !== 'localStorage' && eventName !== 'data-ab-name' && eventName !== 'unknown';
    if (source === 'window' && isRealEvent) {
        return `window.dispatchEvent(new CustomEvent('${eventName}', { detail: { ABTest: '${id}' } }));`;
    }
    if (id && id !== 'n/a') {
        return `localStorage.setItem('ab_test_${id}', 'B'); if (window.activeAbTest) window.activeAbTest('${id}');`;
    }
    return '';
};

const copyToClipboard = (text: string): Promise<void> => {
    if (navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(text);
    }
    // Fallback pour les contextes non sécurisés (dev en http)
    return new Promise((resolve, reject) => {
        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(textarea);
            ok ? resolve() : reject(new Error('copy failed'));
        } catch (e) {
            reject(e);
        }
    });
};

const isAllowedToShow = (): boolean => {
    if (navigator.userAgent === 'PlaywrightE2E') return false;

    const env = document.body?.dataset?.env;
    const isNonProd = Boolean(env) || window.location.origin !== 'https://www.tryba.com';
    let forced = false;
    try {
        forced = window.localStorage.getItem(DEBUG_FLAG_KEY) === '1';
    } catch (e) {
        forced = false;
    }
    return isNonProd || forced;
};

const ensurePanel = (): HTMLElement | null => {
    const existing = document.getElementById(PANEL_ID);
    if (existing) return existing;

    const container = document.createElement('div');
    container.id = PANEL_ID;
    document.body.appendChild(container);
    return container;
};

const hasClosedCookie = (): boolean => {
    return document.cookie.split(';').some((item) => item.trim().startsWith(`${CLOSED_KEY}=`));
};

const isClosedForSession = (): boolean => {
    try {
        return window.sessionStorage.getItem(CLOSED_KEY) === '1' || hasClosedCookie();
    } catch (e) {
        return hasClosedCookie();
    }
};

const setClosedForSession = (): void => {
    try {
        window.sessionStorage.setItem(CLOSED_KEY, '1');
    } catch (e) {
        // ignore
    }
    document.cookie = `${CLOSED_KEY}=1; path=/`;
};

const getCollapsed = (): boolean => {
    try {
        const stored = window.sessionStorage.getItem(COLLAPSED_KEY);
        if (stored === null) return true;
        return stored === '1';
    } catch (e) {
        return true;
    }
};

const setCollapsed = (collapsed: boolean): void => {
    try {
        window.sessionStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch (e) {
        // ignore
    }
};

const renderPanel = (container: HTMLElement): void => {
    if (isClosedForSession()) {
        container.remove();
        return;
    }

    const snapshot = getAbRegistrySnapshot();
    const collapsed = getCollapsed();
    const rows = snapshot.entries
        .map((entry) => {
            const statusLabel = entry.active ? 'active' : 'inactive';
            const statusStyle = entry.active ? 'color: #15803d;' : 'color: #6b7280;';
            const idLabel = entry.id ?? 'n/a';
            const eventLabel = entry.event ?? 'n/a';
            const sourceLabel = entry.source;
            const label = entry.label || idLabel;
            const abTastyCode = buildAbTastyCode(idLabel, eventLabel, sourceLabel);

            return `
                <div class="ab-debug-row flex items-start gap-2 px-3 py-2 border-b border-black/5 last:border-b-0">
                    <span class="mt-0.5 h-2 w-2 rounded-full" style="background: ${
                        entry.active ? '#22c55e' : '#9ca3af'
                    };"></span>
                    <div class="flex-1">
                        <div class="font-semibold text-[11px]">${escapeHtml(label)}</div>
                        <div class="text-[10px] text-gray-600">ID: ${escapeHtml(idLabel)}</div>
                        <div class="text-[10px] text-gray-600">Event: ${escapeHtml(eventLabel)}</div>
                        <div class="text-[10px] text-gray-500">Source: ${escapeHtml(sourceLabel)}</div>
                    </div>
                    <div class="flex flex-col items-end gap-1">
                        <div class="text-[10px] font-semibold uppercase" style="${statusStyle}">${statusLabel}</div>
                        ${
                            entry.active
                                ? ''
                                : `
                        <button
                            class="ab-debug-activate text-[10px] font-semibold text-blue-700 underline"
                            data-ab-id="${escapeHtml(idLabel)}"
                            data-ab-event="${escapeHtml(eventLabel)}"
                            data-ab-source="${escapeHtml(sourceLabel)}"
                            type="button"
                        >
                            Activer
                        </button>
                        `
                        }
                        ${
                            abTastyCode
                                ? `
                        <button
                            class="ab-debug-copy text-[10px] font-semibold text-gray-700 underline"
                            data-ab-code="${escapeHtml(abTastyCode)}"
                            type="button"
                        >
                            Copier
                        </button>
                        `
                                : ''
                        }
                    </div>
                </div>
            `;
        })
        .join('');

    container.innerHTML = `
        <div class="fixed bottom-4 left-4 z-[9999] w-80 max-w-[90vw] overflow-hidden rounded-md border border-black/10 bg-white/95 shadow-lg backdrop-blur">
            <div class="ab-debug-toggle flex items-center justify-between border-b border-black/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide cursor-pointer">
                <div class="flex items-center gap-2">
                    <span>AB Tests</span>
                    <span class="h-2 w-2 rounded-full" style="background: ${snapshot.activeCount > 0 ? '#22c55e' : '#9ca3af'};"></span>
                </div>
                <div class="flex items-center gap-3">
                    <span style="color: ${snapshot.activeCount > 0 ? '#15803d' : '#6b7280'}">Active: ${snapshot.activeCount}</span>
                    <button class="ab-debug-close text-[10px] font-semibold uppercase text-gray-500" type="button">Fermer</button>
                </div>
            </div>
            <div class="ab-debug-body ${collapsed ? 'hidden' : ''}">
                <div class="max-h-64 overflow-auto">${rows}</div>
            </div>
        </div>
    `;
};

const startPanel = (): void => {
    const allowed = isAllowedToShow();
    if (!allowed) return;

    initAbRegistry();
    const container = ensurePanel();
    if (!container) return;

    renderPanel(container);

    onAbRegistryUpdate(() => {
        renderPanel(container);
    });

    container.addEventListener('click', (event) => {
        const target = event.target as HTMLElement | null;
        if (!target) return;

        const closeButton = target.closest('.ab-debug-close') as HTMLElement | null;
        if (closeButton) {
            setClosedForSession();
            renderPanel(container);
            return;
        }

        const toggle = target.closest('.ab-debug-toggle') as HTMLElement | null;
        if (toggle) {
            const collapsed = getCollapsed();
            setCollapsed(!collapsed);
            renderPanel(container);
            return;
        }

        const copyButton = target.closest('.ab-debug-copy') as HTMLElement | null;
        if (copyButton) {
            const code = copyButton.dataset.abCode || '';
            if (!code) return;
            const original = copyButton.textContent;
            copyToClipboard(code)
                .then(() => {
                    copyButton.textContent = 'Copié !';
                })
                .catch(() => {
                    copyButton.textContent = 'Erreur';
                })
                .finally(() => {
                    window.setTimeout(() => {
                        copyButton.textContent = original;
                    }, 1500);
                });
            return;
        }

        const activateButton = target.closest('.ab-debug-activate') as HTMLElement | null;
        if (activateButton) {
            const id = activateButton.dataset.abId || '';
            const eventName = activateButton.dataset.abEvent || '';
            const source = activateButton.dataset.abSource || '';

            if (source === 'window' && eventName && eventName !== 'localStorage' && eventName !== 'data-ab-name') {
                window.dispatchEvent(new CustomEvent(eventName, { detail: { ABTest: id } }));
                activateAbTestByEvent(eventName, { ABTest: id });
                return;
            }

            if (id && id !== 'n/a') {
                const localStorageKey = `ab_test_${id}`;
                try {
                    window.localStorage.setItem(localStorageKey, 'B');
                } catch (e) {
                    // ignore
                }
                if (typeof (window as unknown as { activeAbTest?: (name: string) => void }).activeAbTest === 'function') {
                    (window as unknown as { activeAbTest?: (name: string) => void }).activeAbTest?.(id);
                } else {
                    activateAbTestById(id, 'localStorage');
                }
            }
        }
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startPanel);
} else {
    startPanel();
}
